import {GridSortModel} from '@mui/x-data-grid';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import _ from 'lodash';
import {
  LeftPanelDataResponse,
  LeftPanelReportResponse,
  ReviewerProgressPayload,
} from '../../../../../api/types/reportingApi.type';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {
  AccordionSection,
  FilterComponent,
  Reports,
  Section,
} from '../../config/reportTypes';

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

export const updateApplyButtonState = (
  report: Reports,
  filterValues: Record<string, any>
) => {
  // reviewers is required
  const isReviewerSelected: boolean =
    Array.isArray(filterValues.selectedReviewers) &&
    filterValues.selectedReviewers.some((v) => v === true);

  // Assignment Group is optional
  const isAssignmentsSelected: boolean =
    Array.isArray(filterValues.selectedAssignments) &&
    filterValues.selectedAssignments.length > 0;

  // Only assignments are required (matching Batch Management pattern but without sources)
  const applyButtonDisabled = !isReviewerSelected;

  const config = report.config;
  const configObject = {
    ...config,
    selectionCriteria: {
      ...config.selectionCriteria,
      actions: config.selectionCriteria.actions.map((action) => {
        if (
          action.input_type === 'button' &&
          action.actionKey === 'applyChanges'
        ) {
          return {
            ...action,
            disabled: applyButtonDisabled,
          };
        } else if (
          action.input_type === 'button' &&
          action.actionKey === 'reset'
        ) {
          // Reset button enabled if any filter is selected (matching Batch Management pattern)
          return {
            ...action,
            disabled: !isReviewerSelected && !isAssignmentsSelected,
          };
        } else {
          return action;
        }
      }),
    },
  };
  return {
    ...report,
    config: configObject,
  };
};

const getFolderIds = (
  arr: TreeViewBaseItem<ExtendedTreeItemProps>[]
): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });

  return folderIds;
};

interface SourceItem {
  id: string;
  text: string;
  parent: string;
  isGroup: boolean;
  type: string | null;
  searchGroupTypeCode: string | null;
  a_attr: Record<string, unknown> | null;
  isSelected: boolean;
  state: Record<string, unknown> | null;
  li_attr: Record<string, unknown> | null;
  saveSearchSecurityGroupId: number;
  documentCount: number;
  textWithoutCount: string | null;
}

export const transformDataToTree = (
  data: SourceItem[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  if (!Array.isArray(data)) return [];

  const map = new Map<string, ExtendedTreeItemProps>();

  data?.forEach((item, index) => {
    const nodeId = item.id && item.id.trim() !== '' ? item.id : `node-${index}`;
    const node: ExtendedTreeItemProps = {
      id: nodeId,
      label: item.text || `Unnamed-${index}`,
      name: (item.text || '').toLowerCase().replace(/\s+/g, '-'),
      fileType: item.isGroup ? 'folder' : 'file',
      value: nodeId,
    };
    map.set(nodeId, node);
  });

  const tree: ExtendedTreeItemProps[] = [];
  data?.forEach((item) => {
    const nodeId = item.id || `node-${data.indexOf(item)}`;
    if (item.parent === '#') {
      const rootNode = map.get(nodeId);
      if (rootNode) tree.push(rootNode);
    } else {
      const parentNode = map.get(item.parent);
      const childNode = map.get(nodeId);
      if (parentNode && childNode) {
        if (!parentNode.children) parentNode.children = [];
        parentNode.children.push(childNode);
      }
    }
  });
  return tree;
};

export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: LeftPanelDataResponse,
  savedReportData?: any
) => {
  const config = report.config;
  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'reviewerList'
            ) {
              const transformedData = apiData.reviewerList.map((reviewer) => ({
                label: reviewer.reviewer_Name,
                value: (savedReportData?.selectedReviewers ?? []).includes(
                  reviewer.reviewerID
                ),
              }));
              return {
                ...comp,
                childLabels: transformedData,
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'assignments'
            ) {
              const transformedData = transformDataToTree(
                apiData.lstAssignmentGroup
              );
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: (savedReportData?.selectedAssignments ?? []).map(
                  (id: any) => id.toString()
                ),
              };
            }
            return comp;
          }),
        };
      }),
    })),
  };

  return {
    ...report,
    config: {
      selectionCriteria: config.selectionCriteria,
      sections: configObject.sections,
    },
  };
};

export const getSaveReviewProgressReportDataPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: LeftPanelDataResponse,
  pageNumber: number = 1,
  pageSize: number = 50
) => {
  const payload: ReviewerProgressPayload = {
    SelectedAssignments: [],
    SelectedReviewers: [],
    FromDateTime: _.get(filterValues, 'fromDateTime', ''),
    ToDateTime: _.get(filterValues, 'toDateTime', ''),
    PageNumber: pageNumber,
    PageSize: pageSize,
    ReportTypeCode: 'RPR002',
    SelectedCustomColumns: _.get(filterValues, 'selectedCustomColumns', []),
  };

  // Iterate through the report config to map filter values to payload based on section filter component
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          // selectedAssignments is already an array of strings
          payload.SelectedAssignments = _.get(filterValues, key, []);
        } else if (key === 'selectedReviewers') {
          const selectedReviewers: string[] = [];
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                selectedReviewers.push(
                  _.get(
                    apiData.reviewerList[index],
                    'reviewerID',
                    ''
                  ).toString()
                );
              }
            }
          );
          payload.SelectedReviewers = selectedReviewers;
        }
      });
    });
  });

  return payload;
};

export const getSaveRPRReportDataPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: LeftPanelDataResponse,
  reportId: number = 0,
  pageNumber: number = 1,
  pageSize: number = 50,
  reportTypeCode?: string
) => {
  const selectedAssignments: string[] = [];
  const selectedReviewers: string[] = [];

  // Iterate through the report config to map filter values to payload
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          selectedAssignments.push(..._.get(filterValues, key, []));
        } else if (key === 'selectedReviewers') {
          // Convert boolean array to classification ID strings
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                const reviewerID = _.get(
                  apiData.reviewerList[index],
                  'reviewerID',
                  0
                ).toString();
                selectedReviewers.push(reviewerID);
              }
            }
          );
        }
      });
    });
  });

  return {
    reportId: reportId,
    reportTypeCode: reportTypeCode || apiData?.reportTypeCode || 'RPR002',
    selectedAssignments: selectedAssignments,
    selectedReviewers: selectedReviewers,
    fromDateTime: _.get(filterValues, 'fromDateTime', ''),
    toDateTime: _.get(filterValues, 'toDateTime', ''),
    include_Assignment_Detail: 1,
    pageNumber: pageNumber,
    pageSize: pageSize,
    documentsNotinAnyAssignment: _.get(
      filterValues,
      'documentsNotinAnyAssignment',
      false
    ),
    selectedCustomColumns: _.get(filterValues, 'selectedCustomColumns', []),
    isSavedSearchSource: false,
  };
};

export const getDefaultSorting = (): GridSortModel => {
  return [
    {
      field: 'reviewer_Name',
      sort: 'asc',
    },
  ];
};

export const getAppliedFilterValueObject = (
  apiData: LeftPanelDataResponse,
  savedReportData?: LeftPanelReportResponse
) => {
  const filterObject: {[key: string]: any} = {};

  if (!savedReportData) return filterObject;

  filterObject.selectedAssignments = (
    savedReportData?.selectedAssignments ?? []
  ).map((id) => id.toString());

  filterObject.selectedReviewers = apiData.reviewerList.map((reviewer) =>
    (savedReportData?.selectedReviewers ?? []).includes(reviewer.reviewerID)
  );

  return filterObject;
};

export const getReviewProgressReviewerExportPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: LeftPanelDataResponse,
  selectedCustomColumns: number[] = [],
  reportTypeCode?: string
) => {
  const SelectionCriteria = {
    SelectedReviewers: [] as string[],
    SelectedAssignments: [] as string[],
    FromDateTime: _.get(filterValues, 'fromDateTime', ''),
    ToDateTime: _.get(filterValues, 'toDateTime', ''),
    SelectedCustomColumns: selectedCustomColumns,
    DataAsOf: new Date().toISOString(),
    SelectedFoldersOrGroups: [],
    TimeOffset: '00',
  };

  // Iterate through the report config to map filter values to payload
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          SelectionCriteria.SelectedAssignments = _.get(filterValues, key, []);
        } else if (key === 'selectedReviewers') {
          // Convert boolean array to classification ID strings
          const selectedReviewers: string[] = [];
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                const reviewerID = _.get(
                  apiData.reviewerList[index],
                  'reviewerID',
                  0
                );
                selectedReviewers.push(reviewerID.toString());
              }
            }
          );
          SelectionCriteria.SelectedReviewers = selectedReviewers;
        }
      });
    });
  });

  return {
    SelectionCriteria: SelectionCriteria,
    ActionType: 'EXPORTREPORT',
    ReportType: reportTypeCode || 'RPR002',
  };
};
